/*
 * EmailPreferences — public themed page for mailer subscribers to
 * unsubscribe / re-subscribe. Reached from the email footer link at
 * `/api/mailer/unsubscribe/{sub_id}` which:
 *   1. immediately flips the subscriber to `unsubscribed` (RFC 8058
 *      one-click semantics), then
 *   2. redirects here so the user sees a themed confirmation and can
 *      re-subscribe if they clicked by mistake.
 *
 * Visual language mirrors the /test/coin-reveal "Welcome screen"
 * (SacredEmblem + AmbientStarfield + ob-* styling in
 * components/rewards/OnboardingBookends.css).
 *
 * The sub_id param is the auth material — no login required. It is an
 * opaque MongoDB ObjectId from the emailed link.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import AmbientStarfield from "../components/rewards/shared/AmbientStarfield";
import SacredEmblem from "../components/rewards/shared/SacredEmblem";
import "../components/rewards/OnboardingBookends.css";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailPreferences() {
  const { sub_id } = useParams();
  const [state, setState] = useState({ loading: true, status: null, email: "", name: "", error: null });
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sub_id || sub_id === "invalid") {
        if (!cancelled) setState({ loading: false, error: "invalid" });
        return;
      }
      try {
        const r = await axios.get(`${API}/api/mailer/preferences/${sub_id}`);
        if (cancelled) return;
        setState({
          loading: false,
          status: r.data.status,
          email: r.data.email,
          name: r.data.name || "",
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false,
          error: e?.response?.status === 404 ? "not_found" : "network",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [sub_id]);

  const handleToggle = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      const r = await axios.post(`${API}/api/mailer/preferences/${sub_id}/toggle`);
      setState((s) => ({ ...s, status: r.data.status }));
    } catch (e) {
      setState((s) => ({ ...s, error: "toggle_failed" }));
    } finally {
      setToggling(false);
    }
  };

  // ── error states ─────────────────────────────────────────────────
  if (state.error === "invalid" || state.error === "not_found") {
    return (
      <div className="ob-scene" data-testid="email-preferences-notfound">
        <AmbientStarfield />
        <div className="ob-panel">
          <div className="ob-emblem-wrap"><SacredEmblem size={170} /></div>
          <div className="ob-eyebrow ob-rise">Agent Eros</div>
          <h1 className="ob-title ob-rise ob-rise-1">Preferences Link Invalid</h1>
          <p className="ob-body ob-rise ob-rise-2">
            This subscription link is invalid or the subscriber record no longer exists.
            If you were trying to unsubscribe, please reply to the original email and we&apos;ll
            take care of it manually.
          </p>
        </div>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="ob-scene" data-testid="email-preferences-loading">
        <AmbientStarfield />
        <div className="ob-panel">
          <div className="ob-eyebrow ob-rise">Loading preferences…</div>
        </div>
      </div>
    );
  }

  const isSubscribed = state.status === "subscribed";
  const isBounced = state.status === "bounced";

  // ── main state ───────────────────────────────────────────────────
  return (
    <div className="ob-scene" data-testid="email-preferences">
      <AmbientStarfield />
      <div className="ob-panel">
        <div className="ob-emblem-wrap"><SacredEmblem size={170} /></div>
        <div className="ob-eyebrow ob-rise">Agent Eros</div>

        <h1 className="ob-title ob-rise ob-rise-1" data-testid="email-preferences-title">
          {isSubscribed
            ? "You're Subscribed"
            : isBounced
              ? "Email Bouncing"
              : "You've Been Unsubscribed"}
        </h1>

        <p className="ob-body ob-rise ob-rise-2" data-testid="email-preferences-body">
          {isSubscribed ? (
            <>
              <strong style={{ color: "#e9c98a" }}>{state.email}</strong> is currently receiving
              email from Agent Eros. If you&apos;d rather stop hearing from us, you can unsubscribe below —
              you can always come back and re-subscribe later.
            </>
          ) : isBounced ? (
            <>
              We&apos;ve stopped sending to <strong style={{ color: "#e9c98a" }}>{state.email}</strong>{" "}
              because messages were bouncing. If that inbox is working again, click
              &ldquo;Re-subscribe&rdquo; below to resume.
            </>
          ) : (
            <>
              We&apos;ve unsubscribed <strong style={{ color: "#e9c98a" }}>{state.email}</strong>{" "}
              from Agent Eros emails. You won&apos;t receive any further campaigns or drip messages.
              If this was a mistake, tap Re-subscribe below.
            </>
          )}
        </p>

        <img
          src="/rewards/icon-divider5.png"
          alt=""
          aria-hidden
          className="ob-divider ob-rise ob-rise-25"
        />

        <button
          className="ob-cta ob-rise ob-rise-4"
          onClick={handleToggle}
          disabled={toggling}
          data-testid="email-preferences-toggle-btn"
        >
          {toggling
            ? "Please wait…"
            : isSubscribed
              ? "UNSUBSCRIBE ME"
              : "RE-SUBSCRIBE ME"}
        </button>

        <div className="ob-sub ob-rise ob-rise-5" data-testid="email-preferences-hint">
          {isSubscribed
            ? "You can change your mind any time."
            : "Sequence progress is preserved — you'll pick up where you left off."}
        </div>

        {state.error === "toggle_failed" && (
          <div
            className="ob-rise ob-rise-5"
            style={{ color: "#ef8a8a", fontSize: 13, marginTop: 18 }}
            data-testid="email-preferences-error"
          >
            Something went wrong updating your preference. Please try again.
          </div>
        )}
      </div>
    </div>
  );
}
